import SignClient from '@walletconnect/sign-client';
import type { SessionTypes } from '@walletconnect/types';

import { CHAIN_ID_BY_KEY, type SupportedEvmChain } from './chainList';

let wcClientPromise: Promise<SignClient> | null = null;
let subscribed = false;

export type WalletConnectSessionProposal = {
  id: number;
  proposerName: string;
  proposerUrl: string;
  proposerDescription: string;
  proposerIcons: string[];
  requiredNamespaces: Record<string, { chains?: string[]; methods: string[]; events: string[] }>;
  optionalNamespaces: Record<string, { chains?: string[]; methods: string[]; events: string[] }>;
  raw: unknown;
};

export type WalletConnectSessionRequest = {
  topic: string;
  id: number;
  chainId: string;
  method: string;
  params: unknown;
  raw: unknown;
};

export type WalletConnectChainOffer = {
  /** CAIP-2 ids the wallet can approve (intersection with dApp request when present). */
  offeredChains: string[];
  /** Required chains that the wallet does not support. */
  missingRequiredChains: string[];
  /** Suggested default active chain (CAIP-2). */
  defaultActiveChain: string;
};

const ACTIVE_CHAIN_PREFIX = 'cointrader_wc_active_chain:';

const proposalListeners = new Set<(p: WalletConnectSessionProposal) => void>();
const requestListeners = new Set<(r: WalletConnectSessionRequest) => void>();

const WALLET_BASE_METHODS = [
  'eth_accounts',
  'eth_requestAccounts',
  'eth_chainId',
  'personal_sign',
  'eth_sign',
  'eth_signTypedData',
  'eth_signTypedData_v3',
  'eth_signTypedData_v4',
  'wallet_switchEthereumChain',
  'wallet_addEthereumChain',
] as const;

const WALLET_BASE_EVENTS = ['chainChanged', 'accountsChanged'] as const;

function isWalletConnectUri(uri: string): boolean {
  return /^wc:/i.test(uri.trim());
}

/** All EVM chains this wallet can expose over WalletConnect. */
export function walletSupportedEip155Chains(): string[] {
  return Object.values(CHAIN_ID_BY_KEY).map((v) => `eip155:${v.toString()}`);
}

export function normalizeEip155ChainId(raw: string): string | null {
  const t = raw.trim();
  if (/^eip155:\d+$/i.test(t)) {
    return `eip155:${t.slice('eip155:'.length)}`;
  }
  if (/^\d+$/.test(t)) {
    return `eip155:${t}`;
  }
  if (/^0x[0-9a-fA-F]+$/.test(t)) {
    try {
      return `eip155:${BigInt(t).toString(10)}`;
    } catch {
      return null;
    }
  }
  return null;
}

export function eip155ChainToHex(caip: string): string | null {
  const n = normalizeEip155ChainId(caip);
  if (!n) {
    return null;
  }
  const id = BigInt(n.slice('eip155:'.length));
  return `0x${id.toString(16)}`;
}

export function chainKeyForEip155(caip: string): SupportedEvmChain | undefined {
  const n = normalizeEip155ChainId(caip);
  if (!n) {
    return undefined;
  }
  const id = n.slice('eip155:'.length);
  for (const [key, value] of Object.entries(CHAIN_ID_BY_KEY)) {
    if (value.toString() === id) {
      return key as SupportedEvmChain;
    }
  }
  return undefined;
}

function uniquePreserveOrder(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (seen.has(item)) {
      continue;
    }
    seen.add(item);
    out.push(item);
  }
  return out;
}

function namespaceChains(
  ns: { chains?: string[]; accounts?: string[] } | undefined,
): string[] {
  if (!ns) {
    return [];
  }
  const fromChains = (ns.chains ?? []).map((c) => normalizeEip155ChainId(c)).filter((c): c is string => !!c);
  const fromAccounts = (ns.accounts ?? [])
    .map((a) => {
      const parts = a.split(':');
      if (parts.length >= 2 && parts[0] === 'eip155') {
        return normalizeEip155ChainId(`eip155:${parts[1]}`);
      }
      return null;
    })
    .filter((c): c is string => !!c);
  return uniquePreserveOrder([...fromChains, ...fromAccounts]);
}

/** Compute which chains to offer for a session proposal, and whether required chains are missing. */
export function computeWalletConnectChainOffer(proposal: WalletConnectSessionProposal): WalletConnectChainOffer {
  const supported = new Set(walletSupportedEip155Chains());
  const required = namespaceChains(proposal.requiredNamespaces.eip155);
  const optional = namespaceChains(proposal.optionalNamespaces.eip155);
  const missingRequiredChains = required.filter((c) => !supported.has(c));

  let offered: string[];
  if (required.length || optional.length) {
    const requested = uniquePreserveOrder([...required, ...optional]);
    offered = requested.filter((c) => supported.has(c));
  } else {
    // dApp did not specify chains — expose full wallet support.
    offered = walletSupportedEip155Chains();
  }

  // Prefer Ethereum mainnet as default when available, else first offered chain.
  const defaultActiveChain =
    offered.find((c) => c === 'eip155:1') ?? offered[0] ?? walletSupportedEip155Chains()[0] ?? 'eip155:1';

  return { offeredChains: offered, missingRequiredChains, defaultActiveChain };
}

export function getWalletConnectActiveChain(topic: string): string | undefined {
  try {
    const raw = sessionStorage.getItem(`${ACTIVE_CHAIN_PREFIX}${topic}`)?.trim();
    return raw ? normalizeEip155ChainId(raw) ?? undefined : undefined;
  } catch {
    return undefined;
  }
}

export function setWalletConnectActiveChain(topic: string, chainId: string): void {
  const n = normalizeEip155ChainId(chainId);
  if (!n) {
    return;
  }
  try {
    sessionStorage.setItem(`${ACTIVE_CHAIN_PREFIX}${topic}`, n);
  } catch {
    // ignore quota / private mode
  }
}

/** Parse wallet_switchEthereumChain / wallet_addEthereumChain params into CAIP-2. */
export function parseWalletConnectTargetChain(params: unknown): string | null {
  if (!Array.isArray(params) || params.length < 1 || !params[0] || typeof params[0] !== 'object') {
    return null;
  }
  const obj = params[0] as { chainId?: unknown };
  if (obj.chainId == null) {
    return null;
  }
  return normalizeEip155ChainId(String(obj.chainId));
}

/**
 * Returns true when EIP-712 domain.chainId (if present) matches the session request chain.
 * Missing domain.chainId is treated as compatible.
 */
export function typedDataDomainMatchesRequestChain(
  domain: Record<string, unknown>,
  requestChainId: string,
): boolean {
  if (domain.chainId == null || domain.chainId === '') {
    return true;
  }
  const domainCaip = normalizeEip155ChainId(String(domain.chainId));
  const reqCaip = normalizeEip155ChainId(requestChainId);
  if (!domainCaip || !reqCaip) {
    return false;
  }
  return domainCaip === reqCaip;
}

async function getWalletConnectClient(): Promise<SignClient> {
  if (!wcClientPromise) {
    const projectId = (import.meta.env.VITE_WALLETCONNECT_PROJECT_ID ?? '').trim();
    if (!projectId) {
      throw new Error('WALLETCONNECT_PROJECT_ID_MISSING');
    }
    wcClientPromise = SignClient.init({
      projectId,
      metadata: {
        name: 'Cointrader Wallet',
        description: 'Cointrader wallet connection',
        url: window.location.origin,
        icons: [`${window.location.origin}/favicon.ico`],
      },
    });
  }
  const client = await wcClientPromise;
  if (!subscribed) {
    subscribed = true;
    client.on('session_proposal', (evt) => {
      const p: WalletConnectSessionProposal = {
        id: Number(evt.id),
        proposerName: evt.params.proposer.metadata.name ?? '',
        proposerUrl: evt.params.proposer.metadata.url ?? '',
        proposerDescription: evt.params.proposer.metadata.description ?? '',
        proposerIcons: evt.params.proposer.metadata.icons ?? [],
        requiredNamespaces: evt.params.requiredNamespaces ?? {},
        optionalNamespaces: evt.params.optionalNamespaces ?? {},
        raw: evt,
      };
      proposalListeners.forEach((fn) => fn(p));
    });
    client.on('session_request', (evt) => {
      const r: WalletConnectSessionRequest = {
        topic: evt.topic,
        id: Number(evt.id),
        chainId: evt.params.chainId,
        method: evt.params.request.method,
        params: evt.params.request.params,
        raw: evt,
      };
      requestListeners.forEach((fn) => fn(r));
    });
  }
  return client;
}

/** Pair the scanned wc: URI with WalletConnect relay. */
export async function pairWalletConnectUri(uriRaw: string): Promise<void> {
  const uri = uriRaw.trim();
  if (!isWalletConnectUri(uri)) {
    throw new Error('WALLETCONNECT_URI_INVALID');
  }
  const client = await getWalletConnectClient();
  await client.pair({ uri });
}

export async function ensureWalletConnectReady(): Promise<void> {
  await getWalletConnectClient();
}

export function onWalletConnectSessionProposal(cb: (p: WalletConnectSessionProposal) => void): () => void {
  proposalListeners.add(cb);
  return () => proposalListeners.delete(cb);
}

export function onWalletConnectSessionRequest(cb: (r: WalletConnectSessionRequest) => void): () => void {
  requestListeners.add(cb);
  return () => requestListeners.delete(cb);
}

function orderChainsWithActiveFirst(chains: string[], activeChain: string): string[] {
  const active = normalizeEip155ChainId(activeChain);
  const rest = chains.filter((c) => c !== active);
  return active ? [active, ...rest] : chains;
}

export async function approveWalletConnectSession(
  proposalId: number,
  proposalRaw: unknown,
  eip155Address: string,
  chains: string[],
  activeChain: string,
): Promise<{ topic: string }> {
  if (!chains.length) {
    throw new Error('WALLETCONNECT_NO_CHAINS');
  }
  const client = await getWalletConnectClient();
  const methods = new Set<string>(WALLET_BASE_METHODS);
  const events = new Set<string>(WALLET_BASE_EVENTS);
  const raw = proposalRaw as {
    params?: {
      requiredNamespaces?: Record<string, { methods?: string[]; events?: string[] }>;
      optionalNamespaces?: Record<string, { methods?: string[]; events?: string[] }>;
    };
  };
  const req = raw.params?.requiredNamespaces?.eip155;
  const opt = raw.params?.optionalNamespaces?.eip155;
  (req?.methods ?? []).forEach((m) => methods.add(m));
  (opt?.methods ?? []).forEach((m) => methods.add(m));
  (req?.events ?? []).forEach((m) => events.add(m));
  (opt?.events ?? []).forEach((m) => events.add(m));

  const ordered = orderChainsWithActiveFirst(
    uniquePreserveOrder(chains.map((c) => normalizeEip155ChainId(c)).filter((c): c is string => !!c)),
    activeChain,
  );
  const namespace: SessionTypes.Namespace = {
    accounts: ordered.map((ch) => `${ch}:${eip155Address}`),
    methods: Array.from(methods),
    events: Array.from(events),
  };
  const result = await client.approve({
    id: proposalId,
    namespaces: { eip155: namespace },
  });
  const topic = result.topic;
  setWalletConnectActiveChain(topic, ordered[0] ?? activeChain);
  // Notify dApp of the selected active chain (accounts[0] chain).
  try {
    await client.emit({
      topic,
      event: { name: 'chainChanged', data: eip155ChainToHex(ordered[0] ?? activeChain) },
      chainId: ordered[0] ?? normalizeEip155ChainId(activeChain) ?? 'eip155:1',
    });
  } catch {
    // Some relays ignore early emits before settle ack; session accounts order still hints default chain.
  }
  return { topic };
}

export async function rejectWalletConnectSession(proposalId: number, message = 'User rejected'): Promise<void> {
  const client = await getWalletConnectClient();
  await client.reject({
    id: proposalId,
    reason: { code: 5000, message },
  });
}

export async function respondWalletConnectRequestResult(topic: string, id: number, result: unknown): Promise<void> {
  const client = await getWalletConnectClient();
  await client.respond({
    topic,
    response: { id, jsonrpc: '2.0', result },
  });
}

export async function respondWalletConnectRequestError(topic: string, id: number, code: number, message: string): Promise<void> {
  const client = await getWalletConnectClient();
  await client.respond({
    topic,
    response: { id, jsonrpc: '2.0', error: { code, message } },
  });
}

export async function emitWalletConnectChainChanged(topic: string, chainId: string): Promise<void> {
  const caip = normalizeEip155ChainId(chainId);
  const hex = caip ? eip155ChainToHex(caip) : null;
  if (!caip || !hex) {
    throw new Error('WALLETCONNECT_BAD_CHAIN');
  }
  const client = await getWalletConnectClient();
  setWalletConnectActiveChain(topic, caip);
  await client.emit({
    topic,
    event: { name: 'chainChanged', data: hex },
    chainId: caip,
  });
}

/**
 * Handle non-signing JSON-RPC methods that should not open the payment-password modal.
 * Returns true when the request was fully handled (success or error response already sent).
 */
export async function tryHandleWalletConnectUtilityRequest(
  req: WalletConnectSessionRequest,
  eip155Address: string,
  sessionChains: string[],
): Promise<{ handled: boolean; notice?: 'switched' | 'unsupported_chain' | 'accounts' | 'chain_id' }> {
  const method = req.method;
  const supported = new Set(
    (sessionChains.length ? sessionChains : walletSupportedEip155Chains()).map(
      (c) => normalizeEip155ChainId(c) ?? c,
    ),
  );

  if (method === 'eth_chainId') {
    const active = getWalletConnectActiveChain(req.topic) ?? normalizeEip155ChainId(req.chainId) ?? 'eip155:1';
    const hex = eip155ChainToHex(active);
    if (!hex) {
      await respondWalletConnectRequestError(req.topic, req.id, -32000, 'Invalid active chain');
      return { handled: true };
    }
    await respondWalletConnectRequestResult(req.topic, req.id, hex);
    return { handled: true, notice: 'chain_id' };
  }

  if (method === 'eth_accounts' || method === 'eth_requestAccounts') {
    await respondWalletConnectRequestResult(req.topic, req.id, [eip155Address]);
    return { handled: true, notice: 'accounts' };
  }

  if (method === 'wallet_switchEthereumChain' || method === 'wallet_addEthereumChain') {
    const target = parseWalletConnectTargetChain(req.params);
    if (!target) {
      await respondWalletConnectRequestError(req.topic, req.id, -32602, 'Invalid chainId');
      return { handled: true };
    }
    if (!supported.has(target) && !walletSupportedEip155Chains().includes(target)) {
      // 4902 = unrecognized chain (EIP-3326 / MetaMask convention)
      await respondWalletConnectRequestError(req.topic, req.id, 4902, `Unrecognized chain ${target}`);
      return { handled: true, notice: 'unsupported_chain' };
    }
    if (!supported.has(target)) {
      // Chain is wallet-supported but not in this session — still reject with 4902 so dApp can prompt re-connect.
      await respondWalletConnectRequestError(req.topic, req.id, 4902, `Chain ${target} not in session`);
      return { handled: true, notice: 'unsupported_chain' };
    }
    await emitWalletConnectChainChanged(req.topic, target);
    await respondWalletConnectRequestResult(req.topic, req.id, null);
    return { handled: true, notice: 'switched' };
  }

  return { handled: false };
}

export function sessionChainsFromAccounts(accounts: string[] | undefined): string[] {
  if (!accounts?.length) {
    return [];
  }
  return uniquePreserveOrder(
    accounts
      .map((a) => {
        const parts = a.split(':');
        if (parts.length >= 2 && parts[0] === 'eip155') {
          return normalizeEip155ChainId(`eip155:${parts[1]}`);
        }
        return null;
      })
      .filter((c): c is string => !!c),
  );
}

export async function getWalletConnectSessionChains(topic: string): Promise<string[]> {
  const client = await getWalletConnectClient();
  const session = client.session.get(topic);
  return sessionChainsFromAccounts(session?.namespaces?.eip155?.accounts);
}
